import { useState, useRef, useEffect } from 'react';
import { truncateText } from '@/utils/truncateText';
import type { AgentApiSharedJobPostsItem } from '@/types/serializers';
import Checkbox from '@/components/Checkbox';
import { Button } from '@/components/Button';
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdOpenInNew,
  MdApartment,
  MdWork,
  MdLocationOn,
  MdShare,
  MdLink,
  MdDescription,
  MdBadge,
  MdPerson,
  MdSchool,
} from 'react-icons/md';
import MoneyBagIcon from '@/assets/icons/money_bag.svg?react';
import { ImDatabase } from 'react-icons/im';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import SharedJobPostDetailModal from './SharedJobPostDetailModal';
import useSharedJobPostPdf from '../hooks/useSharedJobPostPdf';

interface Props {
  post: AgentApiSharedJobPostsItem;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disableDetail?: boolean;
  hideShare?: boolean;
}

export default function SharedJobPostCard({
  post,
  isChecked = false,
  onCheckedChange,
  disableDetail = false,
  hideShare = false,
}: Props) {
  const [showRequirements, setShowRequirements] = useState(false);
  const [showRequirementsButton, setShowRequirementsButton] = useState(false);
  const [showFeeNote, setShowFeeNote] = useState(false);
  const [showFeeNoteButton, setShowFeeNoteButton] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState<'job' | 'agent' | 'provider'>('job');
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const { downloadPdf } = useSharedJobPostPdf();

  // 公開URLをクリップボードにコピーして共有する
  const handleCopyPublicUrl = async () => {
    if (!post.public_url) {
      showErrorToast('共有可能なURLがありません');
      return;
    }
    try {
      await navigator.clipboard.writeText(post.public_url);
      showSuccessToast('公開URLをコピーしました');
    } catch {
      showErrorToast('URLのコピーに失敗しました');
    }
  };
  const requirementsRef = useRef<HTMLParagraphElement>(null);
  const feeNoteRef = useRef<HTMLParagraphElement>(null);
  const requirements = post.mandatory_requirement || '特になし';
  const feeNote = post.fee_note;
  const showProviderSource = Boolean(post.provider_company_name?.trim());
  const contentLineClampClass = showProviderSource ? 'line-clamp-2' : 'line-clamp-3';

  useEffect(() => {
    if (requirementsRef.current && requirements) {
      const isOverflowing =
        requirementsRef.current.scrollHeight > requirementsRef.current.clientHeight;
      setShowRequirementsButton(isOverflowing);
    }
  }, [requirements, contentLineClampClass]);

  useEffect(() => {
    if (feeNoteRef.current && feeNote) {
      const isOverflowing = feeNoteRef.current.scrollHeight > feeNoteRef.current.clientHeight;
      setShowFeeNoteButton(isOverflowing);
    }
  }, [feeNote, contentLineClampClass]);

  // 年収の表示を生成
  const formatSalary = () => {
    if (post.salary_info) {
      return post.salary_info;
    }
    if (post.min_salary && post.max_salary) {
      return `${post.min_salary}〜${post.max_salary} 万円`;
    }
    if (post.min_salary) {
      return `${post.min_salary}万円以上`;
    }
    if (post.max_salary) {
      return `${post.max_salary}万円以下`;
    }
    return null;
  };

  const salaryDisplay = formatSalary();

  const requirementAndFeePanels = (
    <>
      {requirements && (
        <div className="flex-1 rounded border border-solid border-border bg-white px-lg py-md transition-colors">
          <div className="flex items-start gap-md">
            <p className="text-heading-xxs font-bold whitespace-nowrap">必須要件</p>
            <div className="flex min-w-0 flex-1 flex-col gap-sm">
              <p
                ref={requirementsRef}
                className={`text-body-xs break-words whitespace-pre-wrap ${
                  !showRequirements ? contentLineClampClass : ''
                }`}
              >
                {requirements}
              </p>
              {showRequirementsButton && (
                <div className="w-fit">
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowRequirements(!showRequirements);
                    }}
                  >
                    {showRequirements ? (
                      <>
                        <MdKeyboardArrowUp className="size-icon-xxs" />
                        閉じる
                      </>
                    ) : (
                      <>
                        <MdKeyboardArrowDown className="size-icon-xxs" />
                        もっと見る
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {feeNote && (
        <div className="flex-1 rounded border border-solid border-border bg-white px-lg py-md transition-colors">
          <div className="flex items-start gap-md">
            <p className="text-heading-xxs font-bold whitespace-nowrap">報酬情報</p>
            <div className="flex min-w-0 flex-1 flex-col gap-sm">
              <p
                ref={feeNoteRef}
                className={`text-body-xs break-words whitespace-pre-wrap ${
                  !showFeeNote ? contentLineClampClass : ''
                }`}
              >
                {feeNote}
              </p>
              {showFeeNoteButton && (
                <div className="w-fit">
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowFeeNote(!showFeeNote);
                    }}
                  >
                    {showFeeNote ? (
                      <>
                        <MdKeyboardArrowUp className="size-icon-xxs" />
                        閉じる
                      </>
                    ) : (
                      <>
                        <MdKeyboardArrowDown className="size-icon-xxs" />
                        もっと見る
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="flex rounded bg-white px-lg py-md shadow-layer1">
        <div className="flex min-w-0 flex-1 flex-col gap-md">
          {/* 1段目 + 2段目: クリックでモーダルを開くエリア */}
          <div
            className={disableDetail ? '' : 'cursor-pointer'}
            onClick={
              disableDetail
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    setModalInitialTab('job');
                    setIsModalOpen(true);
                  }
            }
          >
            {/* 1段目: タイトル、取り込み元リンク */}
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-sm">
                <div
                  className="flex shrink-0 items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={(checked) => {
                      onCheckedChange?.(checked);
                    }}
                  />
                </div>
                <h3 className="block truncate text-heading-sm text-inherit">{post.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                {/* 取り込み元ページへのリンク（jobmiru で host 未解決の場合など url が null のときは非表示 / #3010） */}
                {post.url && (
                  <Button color="tertiary" size="sm" asChild>
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {post.provider_display_name}
                      <MdOpenInNew className="size-icon-xxs" />
                    </a>
                  </Button>
                )}

                {/* 共有ボタン */}
                {!hideShare && (
                  <Popover open={isSharePopoverOpen} onOpenChange={setIsSharePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        color="secondary"
                        size="sm"
                        iconOnly
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MdShare className="size-icon-xxs" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[130px] rounded-sm p-0 shadow-layer3">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsSharePopoverOpen(false);
                            handleCopyPublicUrl();
                          }}
                          className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                        >
                          <MdLink className="size-icon-xxs" />
                          URL共有
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsSharePopoverOpen(false);
                            downloadPdf(post.id);
                          }}
                          className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                        >
                          <MdDescription className="size-icon-xxs" />
                          PDF共有
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* 2段目: メタデータ行（アイコン + テキスト） */}
            <div className="mt-xs flex items-start justify-between gap-1">
              <div className="flex flex-wrap gap-4">
                {/* 企業名 */}
                <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                  <MdApartment size={20} className="shrink-0" />
                  <p className="truncate text-xs">{post.company_name}</p>
                </div>

                {/* ポジション */}
                {post.job_position_name && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <MdWork size={20} className="shrink-0" />
                    <p className="truncate text-xs" title={post.job_position_name}>
                      {truncateText(post.job_position_name)}
                    </p>
                  </div>
                )}

                {/* 年収 */}
                {salaryDisplay && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <ImDatabase size={16} className="shrink-0" />
                    <p className="truncate text-xs">{salaryDisplay}</p>
                  </div>
                )}

                {/* 勤務地（都道府県コードが取れた求人は「全国」「東京都, 大阪府」形式 / Issue #3394） */}
                {post.working_location_label && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <MdLocationOn size={20} className="shrink-0" />
                    <p className="truncate text-xs">{post.working_location_label}</p>
                  </div>
                )}

                {/* 雇用形態 */}
                {post.employment_type_label && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <MdBadge size={20} className="shrink-0" />
                    <p className="truncate text-xs">{post.employment_type_label}</p>
                  </div>
                )}

                {/* 応募可能年齢 */}
                {post.age_label && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <MdPerson size={20} className="shrink-0" />
                    <p className="truncate text-xs">{post.age_label}</p>
                  </div>
                )}

                {/* 最終学歴 */}
                {post.min_education_level_label && (
                  <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                    <MdSchool size={20} className="shrink-0" />
                    <p className="truncate text-xs">{post.min_education_level_label}</p>
                  </div>
                )}

                {/* 理論年収（fee_info） */}
                {post.fee_info && (
                  <div className="flex items-center gap-1.5">
                    <MoneyBagIcon className="size-5 shrink-0" />
                    <p className="truncate text-xs">{post.fee_info}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 3段目: 必須要件 + 報酬情報（掲載元あり時は2段構成＋折りたたみ2行） */}
          {showProviderSource ? (
            <div className="flex flex-col gap-4">
              <div className="flex w-full gap-md">{requirementAndFeePanels}</div>
              <button
                type="button"
                className="flex w-full items-center gap-2 pl-1 text-left hover:opacity-70"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalInitialTab('provider');
                  setIsModalOpen(true);
                }}
              >
                <span className="shrink-0 text-xs font-bold text-[#262626]">掲載元</span>
                <span className="min-w-0 truncate text-xs leading-[1.7] font-normal text-[#262626]">
                  {post.provider_company_name}
                </span>
              </button>
            </div>
          ) : (
            <div className="flex gap-md">{requirementAndFeePanels}</div>
          )}
        </div>
      </div>

      {!disableDetail && (
        <SharedJobPostDetailModal
          post={post}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          initialTab={modalInitialTab}
        />
      )}
    </>
  );
}
